// Catalog of the question blocks offered by the customer-feedback builder.
//
// This builder is deliberately NOT the generic form builder: a venue asking
// "how happy are you with our service?" needs a handful of well-presented
// rating patterns, not 18 XLSForm question types. Every block below therefore
// carries both its data shape (`create`) and the presentation it should get in
// the respondent view, so the builder card and the kiosk preview always agree.

import { v4 as uuidv4 } from "uuid";

export const BlockType = Object.freeze({
   SMILEY: "smiley",
   STARS: "stars",
   MULTISTARS: "multistars",
   NPS: "nps",
   SCALE: "scale",
   LIKERT: "likert",
   THUMBS: "thumbs",
   TAGS: "tags",
   CHOICE: "choice",
   COMMENT: "comment",
   SHORT_TEXT: "shortText",
   NUMBER: "number",
   DATE: "date",
   CONTACT: "contact"
});

// Emoji scales. Each entry is ordered from the worst to the best experience so
// that a 3-point scale is just the 5-point scale with the extremes trimmed.
export const SMILEY_SETS = Object.freeze({
   faces: {
      label: "Faces",
      points: [
         { emoji: "😠", text: "Very unhappy" },
         { emoji: "🙁", text: "Unhappy" },
         { emoji: "😐", text: "Neutral" },
         { emoji: "🙂", text: "Happy" },
         { emoji: "😍", text: "Very happy" }
      ]
   },
   food: {
      label: "Taste",
      points: [
         { emoji: "🤢", text: "Awful" },
         { emoji: "😕", text: "Poor" },
         { emoji: "😐", text: "Average" },
         { emoji: "😋", text: "Tasty" },
         { emoji: "🤤", text: "Delicious" }
      ]
   },
   hearts: {
      label: "Hearts",
      points: [
         { emoji: "💔", text: "Not at all" },
         { emoji: "🧡", text: "A little" },
         { emoji: "💛", text: "It was fine" },
         { emoji: "💚", text: "I liked it" },
         { emoji: "❤️", text: "I loved it" }
      ]
   }
});

/** Which of a set's five points a `scale`-point smiley uses, worst to best. */
const smileyPositions = (scale) => (scale === 3 ? [0, 2, 4] : [0, 1, 2, 3, 4]);

/**
 * A 5-point set trimmed to `scale` points, keeping both extremes.
 * scale 5 -> all, scale 3 -> worst / middle / best.
 */
export const getSmileyPoints = (set, scale) => {
   const points = (SMILEY_SETS[set] ?? SMILEY_SETS.faces).points;
   return smileyPositions(scale).map((position) => points[position]);
};

/**
 * A smiley block's points with the author's wording put over the set's own,
 * worst to best. `position` is the point's place in the full five-point set.
 *
 * `config.labels` holds that wording against those five places rather than
 * against the faces on screen, so switching between 3 and 5 faces keeps what
 * was typed for the faces both scales share. A blank keeps the set's own text:
 * the wording is written as the choice itself, and the save writes no blank
 * choice (`builderApi/diffSurvey`) — leaving the block one face short of what
 * the kiosk draws.
 */
export const smileyPointsOf = (question) => {
   const { set, scale, labels = [] } = question.config ?? {};
   const points = (SMILEY_SETS[set] ?? SMILEY_SETS.faces).points;

   return smileyPositions(scale).map((position) => ({
      ...points[position],
      position,
      text: labels[position]?.trim() || points[position].text
   }));
};

/**
 * Wording read back off a saved smiley question, as `config.labels`. Only text
 * that differs from the set's own is kept, so a face nobody reworded goes on
 * following the set — and changes with it when the author picks another.
 */
export const smileyLabelsFrom = (set, scale, texts = []) => {
   const labels = ["", "", "", "", ""];
   smileyPointsOf({ config: { set, scale } }).forEach((point, index) => {
      if (texts[index]?.trim() && texts[index] !== point.text) {
         labels[point.position] = texts[index];
      }
   });
   return labels;
};

// The scale blocks that run best-first — the happiest face, "Strongly agree" —
// down to the worst: on the kiosk, and in the order their choices are written,
// so the standard preview, the results and the translate screen list them the
// same way. The catalog above stays worst to best, and so does an answer's
// value (a smiley's 0 is still the unhappiest face), which is what the
// low-score half is measured on. Thumbs are written up-first already; stars and
// the numbered scales count upwards like any number line.
const BEST_FIRST_TYPES = [BlockType.SMILEY, BlockType.LIKERT];

/**
 * `items`, in the catalog's worst-to-best order, in the order the block shows
 * and writes them.
 */
export const inShownOrder = (type, items) =>
   BEST_FIRST_TYPES.includes(type) ? [...items].reverse() : items;

// Icon pairs for the thumbs block. Persisted questions are recognised back by
// these exact emoji pairs (see feedbackPayload.js), so additions are safe but
// changes orphan saved ones.
export const THUMB_SETS = Object.freeze({
   thumbs: { label: "Thumbs", up: "👍", down: "👎" },
   check: { label: "Check / cross", up: "✅", down: "❌" },
   faces: { label: "Happy / sad", up: "😊", down: "😞" }
});

export const getThumbIcons = (set) => THUMB_SETS[set] ?? THUMB_SETS.thumbs;

/**
 * The wording on a thumbs block's two sides, never blank.
 *
 * Blank wording would be written as an empty choice, which the save drops
 * (`builderApi/diffSurvey`) — leaving the block one choice short of what the
 * kiosk thinks it has. The same fallback is used when writing the question and
 * when rendering it, so the two always agree.
 */
export const getThumbText = (question) => ({
   up: question.config?.upText?.trim() || "Yes",
   down: question.config?.downText?.trim() || "No"
});

// Agreement labels for the Likert block, worst to best. These exact strings are
// also how a persisted question is recognised as a Likert block when a survey
// is read back (see feedbackPayload.js), so changing them orphans saved ones.
export const LIKERT_LABELS = Object.freeze([
   "Strongly disagree",
   "Disagree",
   "Neutral",
   "Agree",
   "Strongly agree"
]);

/** The 5-point agreement set trimmed to `scale` points. */
export const getLikertLabels = (scale) =>
   scale === 3 ? [LIKERT_LABELS[1], LIKERT_LABELS[2], LIKERT_LABELS[3]] : [...LIKERT_LABELS];

/**
 * Which point each choice of a smiley or agree/disagree block IS, in the order
 * the block writes them — what a saved choice is matched by when the scale
 * changes length.
 *
 * Position cannot be that for these two: going from 3 points to 5 puts the new
 * points BETWEEN the old ones, so the second of three ("Average") is not the
 * second of five ("Liked it"). A face is its place in the full five-point set,
 * so picking another emoji set keeps it; an agreement point is its wording,
 * which the 3-point scale shares with the 5-point one.
 *
 * Null for every other block: stars and the numbered scales count up from 1, so
 * their position already is the point.
 */
export const pointKeysOf = (block) => {
   switch (block?.type) {
      case BlockType.SMILEY:
         return inShownOrder(block.type, smileyPointsOf(block)).map(
            (point) => `face:${point.position}`
         );
      case BlockType.LIKERT:
         return inShownOrder(block.type, getLikertLabels(block.config?.scale)).map(
            (text) => `agreement:${text}`
         );
      default:
         return null;
   }
};

const option = (text) => ({ id: uuidv4(), text });

export const makeOption = option;

/**
 * The block's options with blank ones dropped. This is what the kiosk offers
 * AND what is written to the survey (the save drops empty-text choices anyway),
 * so a half-typed option can never desync the two.
 */
export const selectableOptions = (question) =>
   (question.options ?? []).filter((item) => item.text?.trim());

// The palette. `group` drives the section headers in the builder sidebar.
export const BLOCKS = [
   {
      type: BlockType.SMILEY,
      group: "Ratings & scales",
      name: "Smiley scale",
      icon: "😍",
      hint: "Emoji satisfaction scale — the fastest question to answer on a kiosk.",
      create: () => ({
         label: "How happy are you with our service?",
         required: true,
         config: { set: "faces", scale: 5, showLabels: true }
      })
   },
   {
      type: BlockType.STARS,
      group: "Ratings & scales",
      name: "Star rating",
      icon: "⭐",
      hint: "Classic 1–5 star rating for food, cleanliness or staff.",
      create: () => ({
         label: "How would you rate the food?",
         required: false,
         config: { max: 5 }
      })
   },
   {
      type: BlockType.MULTISTARS,
      group: "Ratings & scales",
      name: "Rate a few things",
      icon: "🌟",
      hint: "Star rows for several aspects — food, service, cleanliness — on one screen.",
      create: () => ({
         label: "How would you rate…",
         required: false,
         options: [option("The food"), option("The service"), option("The atmosphere")],
         config: {}
      })
   },
   {
      type: BlockType.NPS,
      group: "Ratings & scales",
      name: "Recommendation (0–10)",
      icon: "📈",
      hint: "Net Promoter style 0–10 scale with colour-graded buttons.",
      create: () => ({
         label: "How likely are you to recommend us to a friend?",
         required: false,
         config: { lowLabel: "Not likely", highLabel: "Very likely" }
      })
   },
   {
      type: BlockType.SCALE,
      group: "Ratings & scales",
      name: "Rating scale (1–N)",
      icon: "🎚️",
      hint: "Numbered scale with your own end labels — great for effort scores.",
      create: () => ({
         label: "How easy was it to get what you needed?",
         required: false,
         config: { max: 5, lowLabel: "Very difficult", highLabel: "Very easy" }
      })
   },
   {
      type: BlockType.LIKERT,
      group: "Ratings & scales",
      name: "Agree / disagree",
      icon: "⚖️",
      hint: "A statement guests rate from strongly agree to strongly disagree.",
      create: () => ({
         label: "The staff made me feel welcome.",
         required: false,
         config: { scale: 5 }
      })
   },
   {
      type: BlockType.THUMBS,
      group: "Ratings & scales",
      name: "Thumbs up / down",
      icon: "👍",
      hint: "A single yes-or-no tap, good for “would you come back?”.",
      create: () => ({
         label: "Would you visit us again?",
         required: false,
         config: { iconSet: "thumbs", upText: "Yes, for sure", downText: "Probably not" }
      })
   },
   {
      type: BlockType.TAGS,
      group: "Choices",
      name: "Pick the good bits",
      icon: "🏷️",
      hint: "Multi-select chips — respondents tap everything that applies.",
      create: () => ({
         label: "What did you enjoy the most?",
         required: false,
         options: [
            option("The food"),
            option("The staff"),
            option("The atmosphere"),
            option("Value for money"),
            option("Speed of service")
         ],
         config: { allowOther: false, shuffle: false }
      })
   },
   {
      type: BlockType.CHOICE,
      group: "Choices",
      name: "Single choice",
      icon: "🔘",
      hint: "One answer only — visit frequency, table number, branch…",
      create: () => ({
         label: "How often do you visit us?",
         required: false,
         options: [
            option("First time"),
            option("Once in a while"),
            option("Every month"),
            option("Every week")
         ],
         config: { allowOther: false, shuffle: false }
      })
   },
   {
      type: BlockType.COMMENT,
      group: "Open feedback",
      name: "Comment box",
      icon: "💬",
      hint: "Free text for anything the scales can’t capture.",
      create: () => ({
         label: "Anything we could do better?",
         required: false,
         config: { placeholder: "Tell us in your own words…", maxLength: 300 }
      })
   },
   {
      type: BlockType.SHORT_TEXT,
      group: "Open feedback",
      name: "Short answer",
      icon: "✏️",
      hint: "A single line — table number, order number, the dish they had.",
      create: () => ({
         label: "Which dish did you order?",
         required: false,
         config: { placeholder: "Type here…", maxLength: 80 }
      })
   },
   {
      type: BlockType.NUMBER,
      group: "Respondent info",
      name: "Number",
      icon: "🔢",
      hint: "A tap-friendly counter — party size, number of visits…",
      create: () => ({
         label: "How many people are in your party?",
         required: false,
         config: { min: 1, max: 12 }
      })
   },
   {
      type: BlockType.DATE,
      group: "Respondent info",
      name: "Date",
      icon: "📅",
      hint: "A calendar pick — the day they visited or stayed.",
      create: () => ({
         label: "When did you visit us?",
         required: false,
         config: {}
      })
   },
   {
      type: BlockType.CONTACT,
      group: "Respondent info",
      name: "Contact details",
      icon: "📇",
      hint: "Optional name, e-mail or phone so you can follow up.",
      create: () => ({
         label: "Would you like us to get back to you?",
         required: false,
         config: { fields: ["name", "email"] }
      })
   }
];

export const BLOCK_GROUPS = ["Ratings & scales", "Choices", "Open feedback", "Respondent info"];

export const getBlockMeta = (type) => BLOCKS.find((block) => block.type === type);

/** Builds a fresh question for the canvas from a palette entry. */
export const createQuestion = (type) => {
   const meta = getBlockMeta(type);
   if (!meta) return null;

   const { label, required = false, options = [], config = {} } = meta.create();
   // `saved` fills in once the block has been written to the survey; empty is
   // what tells the save this question does not exist there yet.
   return { id: uuidv4(), type, saved: [], label, description: "", required, options, config };
};

export const CONTACT_FIELDS = [
   { key: "name", label: "Name", placeholder: "Your name", inputType: "text" },
   { key: "email", label: "E-mail", placeholder: "you@example.com", inputType: "email" },
   { key: "phone", label: "Phone", placeholder: "+383 …", inputType: "tel" }
];

/** 0–10 recommendation buttons shade from red to green. */
export const npsColor = (score) => {
   if (score <= 6) return "#e5484d";
   if (score <= 8) return "#f5a524";
   return "#2f9e63";
};

// -------------------------------------------------------------- "Other" option

// The choice types that can offer a write-in "Other…" option. It is stored as
// one extra choice labelled `OTHER_LABEL` plus one extra, never-required TEXT
// question directly behind the block's own (the same pattern the low-score
// follow-up uses), so the free text reads like any other comment in results.
export const OTHER_OPTION_ID = "other";
export const OTHER_LABEL = "Other";
export const OTHER_SPECIFY_LABEL = "Other — tell us more";

export const supportsOther = (type) => [BlockType.TAGS, BlockType.CHOICE].includes(type);

export const otherEnabled = (question) =>
   supportsOther(question.type) && Boolean(question.config?.allowOther);

/** Where a block's "Other…" write-in text lives in the kiosk's answers map. */
export const otherKey = (questionId) => `${questionId}:other`;

/** True when this answer includes the "Other…" option. */
export const otherSelected = (question, value) =>
   question.type === BlockType.TAGS
      ? Array.isArray(value) && value.includes(OTHER_OPTION_ID)
      : value === OTHER_OPTION_ID;

// -------------------------------------------------- low-score follow-up

export const DEFAULT_FOLLOW_UP_PROMPT = "Sorry to hear that — what could we have done better?";

// Seed reasons for the tappable follow-up. Venues edit them to match.
export const DEFAULT_FOLLOW_UP_REASONS = [
   "The food",
   "The wait time",
   "The staff",
   "Cleanliness",
   "The prices"
];

// The score blocks that can ask "why?" after a low rating. One list, so the
// settings panel, the saved payload and the submission can never disagree
// about which blocks carry a follow-up.
const FOLLOW_UP_TYPES = [
   BlockType.SMILEY,
   BlockType.STARS,
   BlockType.NPS,
   BlockType.SCALE,
   BlockType.LIKERT,
   BlockType.THUMBS
];

export const supportsFollowUp = (type) => FOLLOW_UP_TYPES.includes(type);

/** The block's follow-up config when the feature is switched on, else null. */
export const followUpOf = (question) =>
   supportsFollowUp(question.type) && question.config?.followUp?.enabled
      ? question.config.followUp
      : null;

/**
 * A follow-up asks its "why?" either as a comment box (`text`, the default) or
 * as tappable reason chips (`reasons`) — the smiley-terminal pattern: low
 * rating → tap what went wrong. A `reasons` follow-up with no usable reasons
 * behaves as `text`, so a half-configured block still works on the kiosk.
 */
export const followUpStyleOf = (followUp) =>
   followUp?.style === "reasons" && followUpReasonsOf(followUp).length > 0 ? "reasons" : "text";

/** The follow-up's reason options with blank ones dropped. */
export const followUpReasonsOf = (followUp) =>
   (followUp?.reasons ?? []).filter((reason) => reason.text?.trim());

/** Where a block's follow-up answer lives in the kiosk's answers map. */
export const followUpKey = (questionId) => `${questionId}:followUp`;

/**
 * True when an answer sits in the unhappy half of its scale — the moment worth
 * asking "why?". Score blocks only; anything else never triggers a follow-up.
 */
export const isLowScore = (question, value) => {
   if (value === null || value === undefined || value === "") return false;

   switch (question.type) {
      case BlockType.SMILEY: {
         const count = getSmileyPoints(question.config.set, question.config.scale).length;
         return value < Math.floor(count / 2);
      }
      case BlockType.LIKERT: {
         const count = getLikertLabels(question.config.scale).length;
         return value < Math.floor(count / 2);
      }
      case BlockType.STARS:
      case BlockType.SCALE:
         return value <= Math.floor((question.config.max ?? 5) / 2);
      case BlockType.NPS:
         // Detractors, matching the red of `npsColor`.
         return value <= 6;
      case BlockType.THUMBS:
         return value === "down";
      default:
         return false;
   }
};

/** True when the kiosk should be showing this question's follow-up box. */
export const showsFollowUp = (question, value) =>
   Boolean(followUpOf(question)) && isLowScore(question, value);

// ----------------------------------------------------------- completion time

// Rough seconds a guest spends on each block, for the builder's length hint.
// Tap questions are near-instant; anything with a keyboard dominates the run.
const SECONDS_PER_BLOCK = {
   [BlockType.SMILEY]: 4,
   [BlockType.STARS]: 4,
   [BlockType.NPS]: 5,
   [BlockType.SCALE]: 4,
   [BlockType.LIKERT]: 5,
   [BlockType.THUMBS]: 3,
   [BlockType.TAGS]: 7,
   [BlockType.CHOICE]: 5,
   [BlockType.COMMENT]: 20,
   [BlockType.SHORT_TEXT]: 12,
   [BlockType.NUMBER]: 5,
   [BlockType.DATE]: 7
};

/** Estimated seconds to complete a run of these questions. */
export const estimateRunSeconds = (questions = []) =>
   questions.reduce((total, question) => {
      if (question.type === BlockType.MULTISTARS) {
         return total + 4 * Math.max(question.options?.length ?? 0, 1);
      }
      if (question.type === BlockType.CONTACT) {
         return total + 8 * Math.max(question.config?.fields?.length ?? 0, 1);
      }
      return total + (SECONDS_PER_BLOCK[question.type] ?? 5);
   }, 0);

/** "~40 s" / "~1½ min" style wording for the length hint. */
export const formatRunSeconds = (seconds) => {
   // Rounded to 5 s below a minute, to half-minutes above it — "~55 s" reads
   // fine, "~60 s" does not.
   if (seconds < 55) return `~${Math.max(Math.round(seconds / 5) * 5, 5)} s`;
   const halves = Math.round(seconds / 30) / 2;
   return `~${String(halves).replace(".5", "½")} min`;
};

// Above this, the builder nudges the author to trim — completion rates on
// kiosks fall off sharply past a handful of questions.
export const KIOSK_QUESTION_BUDGET = 7;
